'use client';
/** @jsxImportSource @emotion/react */

import { type RefObject, useState } from 'react';

import type { MenuProps } from '../../../menu/Menu';

import { Box } from '../../../box/Box';
import { ClickAwayListener } from '../../../click-away-listener/ClickAwayListener';
import { CoreIcon } from '../../../core-icon/CoreIcon';
import { Divider } from '../../../divider/Divider';
import { InputAdornment } from '../../../input-adornment/InputAdornment';
import { MenuList } from '../../../menu-list/MenuList';
import { Paper } from '../../../paper/Paper';
import { Popper } from '../../../popper/Popper';
import { TextField } from '../../../text-field/TextField';

type FilterableMenuProps = Omit<MenuProps, 'anchorEl' | 'onClose'> & {
  anchorEl: HTMLElement | null;
  menuButtonRef: RefObject<HTMLButtonElement | null>;
  onClose: (event: Event | React.SyntheticEvent) => void;
  onSearch: (query: string) => void;
  menuSearchLabel?: string;
  searchQuery?: string;
  onFocusedChange?: (focused: number) => void;
};

export const FilterableMenu = ({
  anchorEl,
  menuButtonRef,
  MenuListProps,
  menuSearchLabel,
  onClose,
  onFocusedChange,
  onSearch,
  open,
  searchQuery: search,
  slotProps,
  ...rest
}: FilterableMenuProps) => {
  const [searchQuery, setSearchQuery] = useState(search ?? '');
  const { paper: paperProps } = slotProps ?? {};

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }
    onClose(event);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose(event);
      menuButtonRef.current?.focus();
    }
  };

  return (
    <Popper
      anchorEl={anchorEl}
      open={open}
      placement="bottom"
      role={undefined}
      sx={{
        /**
         * zIndex is set to ensure proper layering.
         * The issue discussed here provides context for this decision:
         * https://github.com/mui/material-ui/issues/14216
         */
        zIndex: (theme) => theme.zIndex.modal,
      }}
    >
      {() => {
        return (
          <Paper {...paperProps}>
            <ClickAwayListener onClickAway={handleClose}>
              <Box>
                <Box
                  sx={(theme) => ({
                    p: theme.spacing(2),
                  })}
                >
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CoreIcon name="search" />
                        </InputAdornment>
                      ),
                    }}
                    autoComplete="off"
                    label={menuSearchLabel}
                    margin="none"
                    size="small"
                    value={searchQuery}
                    variant="outlined"
                    autoFocus
                    fullWidth
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      onSearch(e.target.value);
                    }}
                    onFocus={(e) => {
                      e.stopPropagation();
                      onFocusedChange?.(-1);
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      handleListKeyDown(e);
                    }}
                  />
                </Box>
                <Divider />
                <MenuList
                  {...MenuListProps}
                  sx={{
                    padding: 0,
                    ...MenuListProps?.sx,
                  }}
                  role="menu"
                  onKeyDown={handleListKeyDown}
                >
                  {rest.children}
                </MenuList>
              </Box>
            </ClickAwayListener>
          </Paper>
        );
      }}
    </Popper>
  );
};
